import type {
  PublicCommunityCartAddItemRequest,
  PublicCommunityCartCheckoutResponse,
  PublicCommunityCartMutationResponse,
  PublicCommunityCartResponse,
} from "@repo/global-types-buddify/community";
import { getCommunityCartToken, setCommunityCartToken } from "./cart-token";

const API_BASE = `${process.env.NEXT_PUBLIC_BUDDIFY_API_URL ?? "http://localhost:3330"}/public/community/cart`;

async function cartFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getCommunityCartToken();
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { "x-community-cart-token": token } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`Community cart API error: ${response.status}`);
  }

  const envelope = (await response.json()) as {
    ok: boolean;
    error: { message: string } | null;
    data: T;
  };

  if (!envelope.ok || envelope.error) {
    throw new Error(envelope.error?.message || "Community cart API returned an error");
  }

  // Mutations mint the token server-side on first write — persist it.
  const maybeToken = (envelope.data as { token?: unknown })?.token;
  if (typeof maybeToken === "string") setCommunityCartToken(maybeToken);

  return envelope.data;
}

export function fetchCommunityCart(): Promise<PublicCommunityCartResponse> {
  return cartFetch<PublicCommunityCartResponse>("");
}

export function fetchCommunityShopCart(
  slug: string,
): Promise<PublicCommunityCartResponse> {
  return cartFetch<PublicCommunityCartResponse>(`/shops/${encodeURIComponent(slug)}`);
}

export function addCommunityCartItem(
  input: PublicCommunityCartAddItemRequest,
): Promise<PublicCommunityCartMutationResponse> {
  return cartFetch<PublicCommunityCartMutationResponse>("/items", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateCommunityCartItem(
  id: string,
  quantity: number,
): Promise<PublicCommunityCartResponse> {
  return cartFetch<PublicCommunityCartResponse>(`/items/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify({ quantity }),
  });
}

export function removeCommunityCartItem(
  id: string,
): Promise<PublicCommunityCartResponse> {
  return cartFetch<PublicCommunityCartResponse>(`/items/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function createCommunityCartCheckout(
  slug: string,
): Promise<PublicCommunityCartCheckoutResponse> {
  return cartFetch<PublicCommunityCartCheckoutResponse>(
    `/shops/${encodeURIComponent(slug)}/checkout`,
    { method: "POST" },
  );
}
